<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { ProductMenuIcon1 as MenuIcon } from '@xpos/icons-svg';
import type { IMenu } from '@posx/core';
import { useMenuStore } from '@/stores/menu';

const { t, locale } = useI18n();
const menuStore = useMenuStore();

const $emit = defineEmits(['change', 'update:modelValue', 'close', 'confirm']);
const $props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const visible = ref($props.modelValue);

// Get available menus from store
const availableMenus = computed(() => menuStore.sortedList);

watch(
  () => $props.modelValue,
  (newVal: boolean) => {
    visible.value = newVal;
  }
);

const handleClose = () => {
  $emit('change', false);
  $emit('update:modelValue', false);
  $emit('close', false);
};

const handleMenuSelect = (menu: IMenu) => {
  $emit('confirm', menu);
  handleClose();
};

onMounted(async () => {
  await menuStore.startLiveQuery();
  if (menuStore.menus.length === 0) await menuStore.getAll();
});
onUnmounted(() => { menuStore.stopLiveQuery(); });
</script>

<template>
  <x-dialog
    v-model="visible"
    width="770px"
    :title="t('menuQrOptions.title')"
    :before-close="handleClose"
    @close="handleClose"
    :close-on-click-modal="false"
  >
    <div class="w-full flex flex-col">
      <!-- Menu Selection label -->
      <div class="mb-[5px]">
        <span class="text-[14px] font-medium text-[#3A3A3A] font-['Barlow'] leading-[130%]">{{ t('menuQrOptions.selectMenu') }}</span>
      </div>

      <!-- Menu Option Buttons with 5px gap -->
      <div class="flex flex-wrap gap-[5px]">
        <x-button
          v-for="menu in availableMenus"
          :key="menu.uid"
          type="primary"
          square
          size="large"
          @click="handleMenuSelect(menu)"
        >
          <el-icon size="50">
            <component :is="MenuIcon" />
          </el-icon>
          <template #footer>
            <span class="text-center text-sm">{{ menu.name_translations?.[locale] || menu.name }}</span>
          </template>
        </x-button>
      </div>
    </div>
  </x-dialog>
</template>

<style lang="scss" scoped>
.el-button + .el-button {
  margin-left: 0;
}
</style>
